/**
 * Client-side paging: the window onto one page of a list the screen already
 * holds.
 *
 * No value imports and no JSX, on the precedent `failure-log.ts` and
 * `analytics-window.ts` set — Node strips types, so this module runs directly
 * under `node`, and the self-check at the foot of it is what
 * `pnpm --filter @empite/admin-portal test` executes. The React half (the state
 * hook and the controls) is in `common.tsx`; this is the arithmetic, which is
 * the part that can be wrong.
 *
 * WHY CLIENT SIDE. The data is already here. `GET /admin/v1/models` returns the
 * whole catalogue in one bounded read — the store caps it at 500 — so paging it
 * is a rendering decision and costs no request. A server cursor would re-fetch
 * rows the browser is holding, and would have to be built on an API that offers
 * none: `user-detail.tsx`'s security events card records what happens when a
 * cursor is faked against an ORDER BY that is not unique, which is that rows
 * written at the same instant are silently skipped.
 *
 * WHY THE PAGE INDEX IS CLAMPED RATHER THAN RESET. The list a screen pages is
 * usually filtered, and a filter can shrink it while somebody is on page six.
 * Clamping on read lands them on the last page in the same render. Resetting in
 * an effect would draw one frame of an empty table first — which reads as "the
 * filter matched nothing", the one conclusion that is wrong.
 */

export interface Paging {
  page: number;
  setPage: (page: number) => void;
  pageSize: number;
}

export interface PagedRows<Row> {
  /** The slice to render. */
  visible: Row[];
  /** The page actually shown, which is `Paging.page` clamped into range. */
  page: number;
  pageCount: number;
  setPage: (page: number) => void;
  /** 1-based, for the "showing X to Y of Z" line. Zero when there is nothing —
   *  "showing 1 to 0 of 0" is arithmetic nobody reads as empty. */
  firstShown: number;
  lastShown: number;
  total: number;
}

export function pageOf<Row>(rows: Row[], paging: Paging): PagedRows<Row> {
  // At least one page even when there are no rows, so `page 1 of 0` is not a
  // thing the controls can render.
  const pageCount = Math.max(1, Math.ceil(rows.length / paging.pageSize));
  const current = Math.min(Math.max(paging.page, 0), pageCount - 1);
  const start = current * paging.pageSize;
  return {
    visible: rows.slice(start, start + paging.pageSize),
    page: current,
    pageCount,
    setPage: paging.setPage,
    firstShown: rows.length === 0 ? 0 : start + 1,
    lastShown: Math.min(start + paging.pageSize, rows.length),
    total: rows.length,
  };
}

// --------------------------------------------------------------------------
// Self-check
// --------------------------------------------------------------------------

export function selfCheck(): void {
  let passed = 0;
  const check = (name: string, actual: unknown, expected: unknown): void => {
    const show = (value: unknown): string =>
      JSON.stringify(value, (_key, item) => (item === undefined ? "<undefined>" : item)) ??
      "<undefined>";
    if (show(actual) !== show(expected)) {
      throw new Error(
        `paging self-check FAILED: ${name}\n  expected ${show(expected)}\n  actual   ${show(actual)}`,
      );
    }
    passed += 1;
  };

  const noop = (): void => {};
  const at = (page: number, pageSize = 10): Paging => ({ page, setPage: noop, pageSize });
  const rows = (n: number): number[] => Array.from({ length: n }, (_, i) => i + 1);

  // ---- the ordinary case --------------------------------------------------
  const first = pageOf(rows(25), at(0));
  check("the first page is the first slice", first.visible, [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]);
  check("and counts from one", [first.firstShown, first.lastShown, first.total], [1, 10, 25]);
  check("three pages for twenty-five rows", first.pageCount, 3);

  const last = pageOf(rows(25), at(2));
  check("the last page is the remainder", last.visible, [21, 22, 23, 24, 25]);
  check("and its count stops at the total", [last.firstShown, last.lastShown], [21, 25]);

  // An exact multiple must not produce a trailing empty page — a "next" button
  // leading to a blank table is the classic off-by-one here.
  check("an exact multiple has no empty tail", pageOf(rows(20), at(0)).pageCount, 2);
  check("and its last page is full", pageOf(rows(20), at(1)).visible.length, 10);

  // ---- clamping -----------------------------------------------------------
  // The case this function exists for: a filter shrank the list under somebody
  // standing on a page that no longer exists. They land on the last one, in the
  // same render, rather than on an empty table that reads as "nothing matched".
  const shrunk = pageOf(rows(12), at(9));
  check("a page past the end clamps to the last", shrunk.page, 1);
  check("and shows that page's rows", shrunk.visible, [11, 12]);
  check("rather than nothing", shrunk.visible.length > 0, true);

  // ---- the empty list -----------------------------------------------------
  const empty = pageOf(rows(0), at(0));
  check("no rows is still one page", empty.pageCount, 1);
  check("with nothing on it", empty.visible, []);
  // Zero and not one: "showing 1 to 0 of 0" is a sentence that has to be
  // parsed before it can be understood to mean empty.
  check("and counts from zero", [empty.firstShown, empty.lastShown, empty.total], [0, 0, 0]);

  // ---- a single page ------------------------------------------------------
  // The controls hide themselves on one page, and this is the value they check.
  check("a short list is one page", pageOf(rows(4), at(0)).pageCount, 1);
  check("and shows all of it", pageOf(rows(4), at(0)).visible, [1, 2, 3, 4]);

  // ---- hostile input ------------------------------------------------------
  // A negative page cannot arrive from the controls, which disable Previous on
  // page zero — but the clamp is two-sided anyway, because a one-sided clamp
  // turns a future caller's mistake into `slice(-10)`, which silently returns
  // the END of the list and looks like a working page.
  check("a negative page clamps to the first", pageOf(rows(25), at(-3)).page, 0);
  check("and does not slice from the end", pageOf(rows(25), at(-3)).visible[0], 1);

  console.log(`paging self-check: ${passed} checks passed`);
}

if ((import.meta as unknown as { main?: boolean }).main === true) {
  selfCheck();
}
